import { getOrderedLessonsFromStream, getComingSoonLessonLocalePackIds, type Stream } from 'Lessons';
import { useContentProgress, getLessonProgressStatus } from 'ContentProgress';
import { useLaunchableLessonsLocalePackIdsByStream } from 'Streams';

export const useNextUpLesson = (stream?: Stream) => {
    const { lessonProgressByLocalePackId } = useContentProgress();
    const lessons = getOrderedLessonsFromStream(stream);
    const launchableLessonLocalePackIds = useLaunchableLessonsLocalePackIdsByStream(stream);
    const comingSoonLessonLocalePackIds = getComingSoonLessonLocalePackIds(stream);
    const completedLessonLocalePackIds = lessons
        .filter(l => getLessonProgressStatus(lessonProgressByLocalePackId[l.localePack.id]) === 'completed')
        .map(lesson => lesson.localePack.id);

    const nextUpLesson = lessons.reduce((prev, curr) => {
        if (completedLessonLocalePackIds.includes(curr.localePack.id)) return prev; // Skip completed lessons
        if (comingSoonLessonLocalePackIds.includes(curr.localePack.id)) return prev; // Skip coming soon lessons
        if (!launchableLessonLocalePackIds.includes(curr.localePack.id)) return prev; // Skip un-launchable lessons

        const prevLessonProgress = lessonProgressByLocalePackId[prev.localePack.id];
        const currLessonProgress = lessonProgressByLocalePackId[curr.localePack.id];

        // If the current lesson has no progress return the previous lesson
        // This gives priority to earlier lessons
        if (!currLessonProgress?.lastProgressAt) return prev;

        // If the current lesson has progress and the previous lesson does not, return the current lesson
        if (currLessonProgress.lastProgressAt && !prevLessonProgress?.lastProgressAt) return curr;

        // Prefer the lesson that was more recently accessed
        if (currLessonProgress.lastProgressAt > (prevLessonProgress?.lastProgressAt ?? 0)) return curr;

        return prev;
    }, lessons.find(l => !completedLessonLocalePackIds.includes(l.localePack.id)) ?? lessons[0]); // Default to first non-completed lesson in stream

    return {
        nextUpLesson,
        hasProgress: !!lessonProgressByLocalePackId[nextUpLesson?.localePack.id],
    };
};
